<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useHead } from "#app";
import content from "./services-content.json";

const meta_tags = ref(content.meta_tags);

useHead({
  title: meta_tags.value.title,
  meta: meta_tags.value.meta,
});

definePageMeta({
  layout: "services-layout",
});

const isLoaded = ref(false);
onMounted(() => {
  setTimeout(() => {
    isLoaded.value = true;
  }, 100);
});

const Tags = ref(
  content.services?.content.reduce((acc, card) => {
    card.tags.forEach((tag) => {
      acc[tag] = (acc[tag] || 0) + 1;
    });
    return acc;
  }, {})
);

const categoryCountMap = computed(() => Tags.value || {});
const checkedCategories = ref([]);
const handleFilterCategory = (filteredList) => {
  checkedCategories.value = filteredList
    .filter((cat) => cat.checked)
    .map((cat) => cat.name);
};

const allCategories = computed(() =>
  [
    "Frameworks",
    "CMS",
    "Mobile Apps",
    "Desktop Apps",
    "AI / ML",
    "Industry Solutions",
    "Full-Stack",
    "Web Development",
    "App Development",
    "Custom Development",
    "E-commerce",
    "Open Source",
    "Cross-Platform",
    "Cloud-based",
    "SaaS",
    "Low-code / No-code",
  ].map((name) => ({
    name,
    checked: false,
    count: categoryCountMap.value[name] || 0,
  }))
);
const searchInput = ref(null);

const handleFocusChange = (value) => {
  if (value) {
    searchInput.value.scrollIntoView({ behavior: "smooth", block: "center" });
    searchInput.value.focus();
  }
};

let worker = null;
const search = ref("");
const workerResults = ref(content.services?.content || []);
let debounceTimer = null;

onMounted(() => {
  worker = new Worker(new URL("./fuseWorker.js", import.meta.url), {
    type: "module",
  });

  worker.onmessage = (e) => {
    const { type, items } = e.data;
    if (type === "results") {
      workerResults.value = items;
    }
  };

  worker.postMessage({
    type: "init",
    payload: { items: content.services?.content || [] },
  });
});

watch(search, (newQuery) => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    if (newQuery.trim()) {
      worker.postMessage({ type: "search", payload: { query: newQuery } });
    } else {
      workerResults.value = content.services?.content || [];
    }
  }, 300);
});

const clearSearch = () => {
  search.value = "";
};

const filteredTabs = computed(() => {
  let tabs = workerResults.value;
  if (checkedCategories.value.length > 0) {
    tabs = tabs.filter((tab) =>
      tab.tags?.some((tag) => checkedCategories.value.includes(tag))
    );
  }
  return tabs;
});

const totalFilteredResults = computed(() =>
  filteredTabs.value.reduce((total, tab) => total + (tab.items?.length || 0), 0)
);

const featuredServices = ref([
  {
    name: "Frameworks",
    icon: "🌐",
    color: "from-blue-500 to-cyan-500",
    link: "/services/frameworks",
  },
  {
    name: "Mobile Apps",
    icon: "📱",
    color: "from-purple-500 to-pink-500",
    link: "/services/mobility",
  },
  {
    name: "AI",
    icon: "🤖",
    color: "from-green-500 to-emerald-500",
    link: "/services/ai",
  },
  {
    name: "Languages",
    icon: "🧑🏻‍💻",
    color: "from-orange-500 to-red-500",
    link: "/services/languages",
  },
  {
    name: "CMS Development",
    icon: "🌐",
    color: "from-indigo-500 to-purple-500",
    link: "/services/cms-development",
  },
  {
    name: "Desktop",
    icon: "🖥️",
    color: "from-teal-500 to-blue-500",
    link: "/services/desktop",
  },
]);

const targetSection = ref(null);
const scrollToTarget = () => {
  if (targetSection.value) {
    const yOffset = -100;
    const y =
      targetSection.value.getBoundingClientRect().top +
      window.pageYOffset +
      yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};
</script>

<template>
  <!-- New Hero Section -->
  <section
    class="relative min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden"
  >
    <!-- Animated Background Grid -->
    <div class="absolute inset-0 opacity-20">
      <div
        class="absolute inset-0"
        style="
          background-image: radial-gradient(
            circle at 1px 1px,
            rgba(255, 255, 255, 0.15) 1px,
            transparent 0
          );
          background-size: 50px 50px;
        "
      ></div>
    </div>

    <!-- Floating Geometric Shapes -->

    <div class="relative z-10 max-w-7xl mx-auto px-4 py-20 mt-10">
      <div class="grid lg:grid-cols-2 gap-12 items-center min-h-screen">
        <!-- Left Content -->
        <div class="space-y-8">
          <!-- Badge -->
          <div
            :class="[
              'inline-flex items-center px-6 py-2 rounded-full border border-white/20 backdrop-blur-sm transition-all duration-1000',
              isLoaded
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4',
            ]"
          >
            <div class="flex items-center gap-3">
              <div class="relative">
                <div
                  class="w-2 h-2 bg-green-400 rounded-full animate-ping absolute"
                ></div>
                <div class="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <span class="text-white/90 font-medium text-sm"
                >{{ content.services?.content.length }}+ Services
                Available</span
              >
            </div>
          </div>

          <!-- Main Heading -->
          <div
            :class="[
              'space-y-4 transition-all duration-1000 delay-200 !mt-2',
              isLoaded
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8',
            ]"
          >
            <h1 class="text-5xl lg:text-7xl font-semibold leading-tight">
              <span class="block text-white mb-2"
                >Your Digital
                <WrGradientText append-class="font-bold max-lg:py-0">
                  Transformation
                </WrGradientText>
                Starts Here
              </span>
            </h1>
          </div>

          <!-- Description -->
          <div
            :class="[
              'transition-all duration-1000 delay-400',
              isLoaded
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8',
            ]"
          >
            <p class="text-xl text-gray-300 leading-relaxed max-w-lg">
              From innovative web solutions to cutting-edge AI implementations,
              we deliver
              <span class="text-purple-400 font-semibold"
                >exceptional digital experiences</span
              >
              that drive your business forward.
            </p>
          </div>

          <!-- CTA Buttons -->
          <div
            :class="[
              'flex flex-wrap gap-4 transition-all duration-1000 delay-600',
              isLoaded
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8',
            ]"
          >
            <WrButton @click="scrollToTarget">
              <span class="relative z-10 flex items-center gap-2">
                Explore Services
                <svg
                  class="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </span>
            </WrButton>

            <div
              class="gradient-border-mask relative before:rounded-full before:bg-[linear-gradient(95.01deg,_#BDB1FF_2.72%,_#6952ED_95.35%)] max-w-max"
            >
              <WrButton
                link="/contact-us"
                outlined
                append-class="z-10 relative text-white"
              >
                <span class="flex items-center gap-2">
                  Get Consultation
                  <svg
                    class="w-5 h-5 group-hover:rotate-12 transition-transform duration-200"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.003 9.003 0 01-8.716-6.747M3 12c0-4.418 4.03-8 9-8a8.997 8.997 0 018.716 6.747"
                    />
                  </svg>
                </span>
              </WrButton>
            </div>
          </div>

          <!-- Stats -->
          <div
            :class="[
              'grid grid-cols-3 gap-8 pt-8 border-t border-white/10 transition-all duration-1000 delay-800',
              isLoaded
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8',
            ]"
          >
            <div class="text-center">
              <div class="text-3xl font-bold text-white mb-1">
                {{ content.services?.content.length }}+
              </div>
              <div class="text-gray-400 text-sm">Services</div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-white mb-1">
                {{ Object.keys(Tags).length }}+
              </div>
              <div class="text-gray-400 text-sm">Technologies</div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-white mb-1">24/7</div>
              <div class="text-gray-400 text-sm">Support</div>
            </div>
          </div>
        </div>

        <!-- Right Visual Section -->
        <div
          class="relative lg:h-screen flex items-center justify-center py-24"
        >
          <!-- Central Hub -->
          <div
            :class="[
              'relative transition-all duration-1000 delay-300',
              isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90',
            ]"
          >
            <!-- Main Central Circle -->
            <div class="relative size-48 md:size-64 mx-auto">
              <div
                class="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-pulse"
              ></div>
              <div
                class="absolute inset-2 bg-slate-900 rounded-full flex items-center justify-center"
              >
                <div class="text-center">
                  <div class="text-4xl mb-2">🚀</div>
                  <div class="text-white font-bold text-lg">Digital</div>
                  <div class="text-purple-400 font-semibold">Solutions</div>
                </div>
              </div>
            </div>

            <!-- Orbiting Service Cards -->
            <div
              v-for="(service, index) in featuredServices"
              :key="service.name"
              :class="[
                'absolute size-24 md:size-32 transition-all duration-1000 top-12 md:top-16 -translate-y-1/2 left-12 md:left-16 -translate-x-1/2',
                isLoaded ? 'opacity-100' : 'opacity-0',
              ]"
              :style="{
                transform: `rotate(${
                  index * 60
                }deg) translateY(-160px) rotate(-${index * 60}deg)`,
                transitionDelay: `${800 + index * 100}ms`,
              }"
            >
              <NuxtLink :to="service.link" class="absolute size-full inset-0" />
              <div
                :class="[
                  'w-full h-full rounded-2xl shadow-2xl flex flex-col items-center justify-center text-white font-semibold text-xs text-center p-2 hover:scale-110 transition-all duration-300 cursor-pointer group bg-gradient-to-br',
                  service.color,
                ]"
              >
                <div
                  class="text-2xl mb-1 group-hover:scale-125 transition-transform duration-200"
                >
                  {{ service.icon }}
                </div>
                <div class="leading-tight text-xs md:text-sm">
                  {{ service.name }}
                </div>
              </div>
            </div>

            <!-- Connecting Lines -->
            <svg
              :class="[
                'absolute inset-0 w-full h-full transition-all duration-1000 delay-1000',
                isLoaded ? 'opacity-30' : 'opacity-0',
              ]"
              viewBox="0 0 400 400"
            >
              <g v-for="i in 6" :key="i">
                <line
                  x1="200"
                  y1="200"
                  :x2="
                    (200 + Math.cos(((i - 1) * Math.PI) / 3) * 140).toFixed(5)
                  "
                  :y2="
                    (200 + Math.sin(((i - 1) * Math.PI) / 3) * 140).toFixed(5)
                  "
                  stroke="url(#gradient)"
                  stroke-width="2"
                  opacity="0.5"
                >
                  <animate
                    attributeName="stroke-dasharray"
                    values="0,200;200,0;0,200"
                    dur="3s"
                    repeatCount="indefinite"
                    :begin="`${i * 0.5}s`"
                  />
                </line>
              </g>
              <defs>
                <linearGradient
                  id="gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    style="stop-color: #8b5cf6; stop-opacity: 1"
                  />
                  <stop
                    offset="100%"
                    style="stop-color: #3b82f6; stop-opacity: 1"
                  />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Enhanced Search Section -->
  <div ref="targetSection" class="relative -mt-8 z-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <div
        class="bg-white dark:bg-[#8542b41d] border border-[#9857c81A] !backdrop-blur-md rounded-2xl px-4 pt-8 pb-16 md:p-8"
      >
        <div class="relative group">
          <div class="relative">
            <input
              v-model="search"
              type="text"
              placeholder="(e.g. Mobile App, Laravel, API Development)"
              class="w-full pl-14 pr-2 py-2.5 md:py-4 rounded-full text-sm md:text-xl text-[var(--foreground)] focus:font-medium placeholder:text-[#958E9F] dark:!bg-transparent !border border-[#b5b5b5] dark:!border-[var(--primary)] shadow-sm focus:outline-none focus:ring-1 focus:ring-[#501d71] focus:border-transparent transition-all duration-300"
            />

            <!-- Search Icon -->
            <div class="absolute left-5 top-1/2 -translate-y-1/2">
              <div
                class="w-8 h-8 bg-[var(--primary)] rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-4 h-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <!-- Clear Button -->
            <button
              v-if="search?.trim()"
              @click="clearSearch"
              class="absolute right-5 top-1/2 -translate-y-1/2 w-8 h-8 bg-gray-200 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg flex items-center justify-center transition-colors duration-200 group"
            >
              <svg
                class="w-4 h-4 text-gray-600 dark:text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Search Results Info -->
        <div
          v-if="search?.trim()"
          class="mt-4 flex items-center justify-between"
        >
          <div class="text-sm">
            <span
              v-if="totalFilteredResults > 0"
              class="text-green-600 dark:text-green-400 font-medium"
            >
              ✨ Found {{ totalFilteredResults }} service{{
                totalFilteredResults !== 1 ? "s" : ""
              }}
              matching "{{ search }}"
            </span>
            <span
              v-else
              class="text-orange-600 dark:text-orange-400 font-medium"
            >
              🔍 No services found matching "{{ search }}". Try different
              keywords.
            </span>
          </div>

          <div
            v-if="checkedCategories.length > 0"
            class="flex items-center gap-2"
          >
            <span class="text-xs text-gray-500">Filtered by:</span>
            <div class="flex gap-1">
              <span
                v-for="category in checkedCategories.slice(0, 2)"
                :key="category"
                class="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs rounded-full"
              >
                {{ category }}
              </span>
              <span
                v-if="checkedCategories.length > 2"
                class="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs rounded-full"
              >
                +{{ checkedCategories.length - 2 }}
              </span>
            </div>
          </div>
        </div>
        <p v-else class="mt-4 p-[10px] text-lg font-medium"></p>
      </div>
    </div>
  </div>

  <!-- Enhanced Main Content -->
  <section class="relative z-20 max-w-screen-xl mx-auto px-4 mt-12">
    <div class="flex items-start max-md:flex-col-reverse gap-8">
      <!-- Enhanced Filter Sidebar -->
      <div class="sm:min-w-[320px] max-md:w-full sticky top-24">
        <WrFilterCapabilities
          @categoryFilter="handleFilterCategory"
          @toggle-more-categories="handleFocusChange"
          :all-categories="allCategories"
          :appendClass="true"
        />
      </div>

      <!-- Results Section -->
      <div class="flex-1 min-w-0">
        <!-- Results Header -->
        <div
          v-if="!search?.trim() && checkedCategories.length === 0"
          class="mb-8"
        >
          <h2
            class="text-3xl font-bold text-gray-900 dark:text-white mb-2"
            ref="searchInput"
          >
            <WrGradientText
              append-class="py-0 bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-3.57%,_var(--text-gradient-2)_20.72%)]"
            >
              Explore Our Services
            </WrGradientText>
          </h2>
          <p class="text-gray-600 dark:text-gray-400">
            Discover our comprehensive range of development services and
            solutions
          </p>
        </div>

        <!-- Service Cards Grid -->
        <div v-if="totalFilteredResults >= 1" class="space-y-8">
          <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            <div
              v-for="(card, index) in filteredTabs"
              :key="index"
              class="transform hover:scale-105 transition-all duration-300"
              :style="{ animationDelay: `${index * 100}ms` }"
            >
              <WrCardList :card="card" :search-term="search" v-if="card" />
            </div>
          </div>
        </div>

        <!-- Enhanced Empty State -->
        <div
          v-if="search?.trim() && totalFilteredResults === 0"
          class="text-center py-16"
        >
          <div class="max-w-md mx-auto">
            <!-- Animated Search Icon -->
            <div class="relative mb-8">
              <div
                class="w-24 h-24 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 rounded-full flex items-center justify-center mx-auto"
              >
                <svg
                  class="w-12 h-12 text-purple-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <div
                class="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center"
              >
                <span class="text-white text-xs">!</span>
              </div>
            </div>

            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              No services found
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-6">
              We couldn't find any services matching "<span
                class="font-semibold text-purple-600"
                >{{ search }}</span
              >". Try searching with different keywords.
            </p>

            <!-- Suggested Keywords -->
            <div class="mb-6">
              <p class="text-sm text-gray-500 mb-3">
                Try these popular searches:
              </p>
              <div class="flex flex-wrap gap-2 justify-center">
                <button
                  v-for="keyword in [
                    'mobile',
                    'web',
                    'API',
                    'maintenance',
                    'AI',
                    'e-commerce',
                  ]"
                  :key="keyword"
                  @click="search = keyword"
                  class="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-sm rounded-full hover:bg-purple-200 dark:hover:bg-purple-800 transition-colors"
                >
                  {{ keyword }}
                </button>
              </div>
            </div>

            <WrButton container-class="mx-auto" @click="clearSearch">
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
              View All Services
            </WrButton>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Enhanced Testimonials Section -->
  <section class="relative z-10 py-20 mt-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <div class="text-center mb-16">
        <h2 class="text-4xl lg:text-5xl font-bold mb-4">
          <WrGradientText
            text="Trusted for excellence and efficiency"
            append-class="bg-gradient-to-r from-gray-900 via-purple-800 to-blue-800 dark:from-white dark:via-purple-200 dark:to-blue-200 bg-clip-text text-transparent"
          />
        </h2>
        <p class="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Webreinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </div>

      <WrTestimonialCardCarousel class="mb-12" />

      <div class="text-center">
        <WrButton link="/testimonials" container-class="mx-auto">
          View All Testimonials
        </WrButton>
      </div>
    </div>
  </section>
</template>

<style scoped>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.grid > div {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}
</style>
